<script setup lang="ts">
import RenderInvoice from '@/components/admin/common/RenderInvoice.vue';
import DropDown, { type DropDownItem } from '@/components/common/DropDown.vue';
import InvoiceSkeleton from '@/components/common/InvoiceSkeleton.vue';
import ProjectsDropDown from '@/components/common/projects/ProjectsDropDown.vue';
import { financialConverter } from '@/converters/financials/financials-converter';
import { adminSupplierInvoiceService } from '@/services/admin/admin-supplier-invoice-service.ts';
import { notificationService } from '@/services/notification/notification-service';
import { fetchAccounts } from '@/utils/accounts/fetch-accounts.ts';
import { FinancialConverterError } from '@/utils/errors/converters/financial-converter-errors';
import {
  accountingConverter,
  isClientError,
  models,
} from 'cesg-shared';
import { SupplierInvoiceStatusType, VatType } from 'cesg-shared/dist/models';
import { computed, onActivated, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteUpdate } from 'vue-router';

const props = defineProps<{
  id: string;
}>();

const { t } = useI18n();

interface FormField {
  id: string;
  label: string;
  value: string;
  paymentType?: 'BG' | 'PG';
}

interface FormDataStructure {
  selectedSupplier: string;
  leftColumn: FormField[];
  rightColumn: FormField[];
  bottomRows: FormField[];
  currentRowId: string | null;
  note: string;
}

type ApiValue = string | number | null | undefined;
type FormColumnKey = 'leftColumn' | 'rightColumn' | 'bottomRows';
type InvoiceFormSource = Omit<
  models.GetAdminSupplierInvoiceResponse,
  'totalAmount' | 'vat' | 'pennyAdjust'
> & {
  totalAmount: string | number;
  vat: string | number;
  pennyAdjust: string | number;
};

const suppliers = ref<models.AdminGetSuppliersResponse>([]);
const accounts = ref<DropDownItem[]>([]);
const isLoading = ref(true);
const serialNumber = ref<string>('-');
const currentSupplierNumber = ref<string>('');
const currentStatus = ref<string>('');
const selectedStatus = ref<string>('');
const showVoidModal = ref(false);
const errorFields = ref<Set<string>>(new Set());
const isReadOnly = ref<boolean>(false);

const formData = reactive<FormDataStructure>({
  selectedSupplier: '',
  currentRowId: null,
  leftColumn: [
    {
      id: 'f1',
      label: t('adminCreateInvoice.leftColumn.organisationNumber'),
      value: '',
    },
    {
      id: 'f2',
      label: t('adminCreateInvoice.leftColumn.bankGiro-plusGiro'),
      value: '',
      paymentType: 'BG',
    },
    {
      id: 'f3',
      label: t('adminCreateInvoice.leftColumn.streetName'),
      value: '',
    },
    {
      id: 'f35',
      label: t('adminCreateInvoice.leftColumn.buildingNumber'),
      value: '',
    },
    {
      id: 'f4',
      label: t('adminCreateInvoice.leftColumn.postNumber'),
      value: '',
    },
    { id: 'f5', label: t('adminCreateInvoice.leftColumn.city'), value: '' },
    {
      id: 'f8',
      label: t('adminCreateInvoice.leftColumn.vatNumber'),
      value: '',
    },
  ],
  rightColumn: [
    {
      id: 'f9',
      label: t('adminCreateInvoice.rightColumn.invoiceDate'),
      value: '',
    },
    {
      id: 'f10',
      label: t('adminCreateInvoice.rightColumn.dueDate'),
      value: '',
    },
    {
      id: 'f11',
      label: t('adminCreateInvoice.rightColumn.termsOfPayment'),
      value: '',
    },
    { id: 'f12', label: t('adminCreateInvoice.rightColumn.ocr'), value: '' },
    {
      id: 'f125',
      label: t('adminCreateInvoice.rightColumn.pennyAdjust'),
      value: '',
    },
    {
      id: 'f13',
      label: t('adminCreateInvoice.rightColumn.totalAmount'),
      value: '',
    },
    {
      id: 'f14',
      label: t('adminCreateInvoice.rightColumn.vat'),
      value: '',
    },
    {
      id: 'f15',
      label: t('adminCreateInvoice.rightColumn.invoiceNumber'),
      value: '',
    },
    {
      id: 'f16',
      label: t('adminCreateInvoice.rightColumn.currency'),
      value: 'SEK',
    },
  ],
  bottomRows: [
    {
      id: 'bottom1',
      label: t('adminCreateInvoice.bottomRows.account'),
      value: '',
    },
    {
      id: 'bottom2',
      label: t('adminCreateInvoice.bottomRows.project'),
      value: '',
    },
    {
      id: 'bottom3',
      label: t('adminCreateInvoice.bottomRows.vatType'),
      value: '',
    },
  ],
  note: '',
});

const supplierOptions = computed<DropDownItem[]>(() => {
  return suppliers.value.map((supplier) => ({
    value: supplier.name,
    text:
      (supplier.supplierNumber ? `${supplier.supplierNumber} - ` : '') +
      supplier.name,
  }));
});

const vatTypeOptions = computed<DropDownItem[]>(() =>
  Object.values(VatType).map((value) => ({
    value,
    text: t(`adminCreateInvoice.bottomRows.vatTypes.${value}`),
  })),
);

const currencyOptions = computed<DropDownItem[]>(() => [
  {
    value: 'SEK',
    text: 'SEK',
  },
]);

const statusOptions = computed<DropDownItem[]>(() => [
  { value: 'incoming', text: t('adminSupplierInvoice.status.incoming') },
  {
    value: 'undercertificate',
    text: t('adminSupplierInvoice.status.undercertificate'),
  },
  { value: 'parked', text: t('adminSupplierInvoice.status.parked') },
  { value: 'disputed', text: t('adminSupplierInvoice.status.disputed') },
]);

const calculateDaysDiff = () => {
  const invoiceDateStr = formData.rightColumn.find((f) => f.id === 'f9')?.value;
  const dueDateStr = formData.rightColumn.find((f) => f.id === 'f10')?.value;
  const daysField = formData.rightColumn.find((f) => f.id === 'f11');

  if (invoiceDateStr && dueDateStr && daysField) {
    const d1 = new Date(invoiceDateStr);
    const d2 = new Date(dueDateStr);

    const diffTime = d2.getTime() - d1.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    daysField.value = diffDays.toString();
  } else if (daysField) {
    daysField.value = '';
  }
};

watch(
  () => [
    formData.rightColumn.find((f) => f.id === 'f9')?.value,
    formData.rightColumn.find((f) => f.id === 'f10')?.value,
  ],
  () => {
    calculateDaysDiff();
  },
);

watch(selectedStatus, (newVal) => {
  if (newVal && newVal !== 'voided' && currentStatus.value === 'voided') {
    currentStatus.value = newVal;
  }
});

const setFormattedFieldValue = (field: FormField, value: ApiValue) => {
  field.value = String(value ?? '');
};

const handleFieldInput = (event: Event, field: FormField) => {
  if (isReadOnly.value) {
    return;
  }

  const target = event.target as HTMLInputElement | null;
  if (!target) {
    return;
  }

  field.value = target.value;

  errorFields.value.delete(field.id);
};

const setFieldVal = (col: FormColumnKey, id: string, val: ApiValue) => {
  const field = formData[col].find((f) => f.id === id);
  if (field) {
    setFormattedFieldValue(field, val);
  }
};

const populateForm = (invoice: InvoiceFormSource) => {
  serialNumber.value = invoice.serialNumber || '';
  currentSupplierNumber.value = invoice.supplierNumber || '';

  setFieldVal('leftColumn', 'f1', invoice.organisationNumber);

  const payField = formData.leftColumn.find((f) => f.id === 'f2');
  if (payField) {
    if (invoice.bankGiro) {
      payField.paymentType = 'BG';
      setFormattedFieldValue(payField, invoice.bankGiro);
    } else if (invoice.plusGiro) {
      payField.paymentType = 'PG';
      setFormattedFieldValue(payField, invoice.plusGiro);
    }
  }
  setFieldVal('leftColumn', 'f4', invoice.zipCode);
  setFieldVal('leftColumn', 'f5', invoice.city);
  setFieldVal('leftColumn', 'f8', invoice.vatNumber);

  setFieldVal('rightColumn', 'f9', invoice.invoiceDate);
  setFieldVal('rightColumn', 'f10', invoice.dueDate);
  setFieldVal('rightColumn', 'f12', invoice.ocr);
  setFieldVal('rightColumn', 'f125', invoice.pennyAdjust);
  setFieldVal('rightColumn', 'f13', invoice.totalAmount);
  setFieldVal('rightColumn', 'f14', invoice.vat);
  setFieldVal('rightColumn', 'f15', invoice.invoiceNumber);
  setFieldVal('rightColumn', 'f16', invoice.currency);

  if (invoice.rows && invoice.rows.length > 0) {
    const firstRow = invoice.rows[0];
    formData.currentRowId = firstRow.id;

    setFieldVal('bottomRows', 'bottom1', firstRow.accountNumber);
    setFieldVal('bottomRows', 'bottom2', firstRow.projectId);
  } else {
    formData.currentRowId = null;
    setFieldVal('bottomRows', 'bottom1', invoice.account);
    setFieldVal('bottomRows', 'bottom2', invoice.projectId);
  }

  setFieldVal('bottomRows', 'bottom3', invoice.vatType);

  formData.note = invoice.notes || '';
  calculateDaysDiff();
};

const uploadedFile = ref<models.SupplierInvoiceFile | null>(null);

const supplierInvoiceToReadable = (
  invoice: models.GetAdminSupplierInvoiceResponse,
) => {
  const { totalAmount, ...invoiceDataWithoutTotal } = invoice;
  return {
    ...financialConverter.toReadable(invoiceDataWithoutTotal),
    totalAmount: financialConverter.readableTotal(totalAmount),
  };
};

const loadData = async (invoiceId: string) => {
  isLoading.value = true;
  try {
    const [fetchedAccounts, fetchedSuppliersRaw, invoiceDataRaw] =
      await Promise.all([
        fetchAccounts(),
        adminSupplierInvoiceService.getAllSuppliers(),
        adminSupplierInvoiceService.getAdminSupplierInvoice(invoiceId),
      ]);

    const fetchedSuppliers = fetchedSuppliersRaw.map((supplier) =>
      financialConverter.toReadable(supplier),
    );

    const invoiceData = supplierInvoiceToReadable(invoiceDataRaw);

    accounts.value = fetchedAccounts;
    setHasProjectId(invoiceData.projectId);

    if (invoiceData.docId && invoiceData.name && invoiceData.blob) {
      uploadedFile.value = {
        fileId: invoiceData.docId,
        name: invoiceData.name,
        blob: invoiceData.blob,
      };
    }

    if (invoiceData) {
      isReadOnly.value =
        (invoiceData.status === SupplierInvoiceStatusType.Approved &&
          invoiceData.booked) ||
        invoiceData.status === SupplierInvoiceStatusType.Paid;

      currentStatus.value = invoiceData.status || '';

      if (invoiceData.status && invoiceData.status !== 'voided') {
        selectedStatus.value = invoiceData.status;
      } else {
        selectedStatus.value = '';
      }

      const invSupNum = invoiceData.supplierNumber;
      const invSupName = invoiceData.supplier || '';

      const match = fetchedSuppliers.find(
        (supplier) =>
          (invSupNum && supplier.supplierNumber === invSupNum) ||
          supplier.name === invSupName,
      );

      if (match) {
        formData.selectedSupplier = match.name;
      } else if (invSupName) {
        fetchedSuppliers.push({
          id: 'temp',
          name: invSupName,
          supplierNumber: invSupNum || '',
          paymentType: 'BG',
          organisationNumber: '',
          streetName: '',
          buildingNumber: '',
          zipCode: '',
          city: '',
          bic: '',
          iban: '',
          vatNumber: '',
          bankGiro: null,
          postGiro: null,
        } as models.AdminGetSuppliersResponse[number]);
        formData.selectedSupplier = invSupName;
      }
    }
    suppliers.value = fetchedSuppliers;
    if (invoiceData) populateForm(invoiceData);
  } catch (error) {
    console.error(t('adminEditInvoice.failedToFetchData'), error);
    notificationService.open({
      text: t('adminCreateInvoice.couldNotFetchData'),
      type: 'danger',
    });
  } finally {
    isLoading.value = false;
  }
};

const reloadCurrentInvoiceData = async () => {
  await loadData(props.id);
};

onMounted(async () => {
  await reloadCurrentInvoiceData();
});

onActivated(async () => {
  await reloadCurrentInvoiceData();
});

// If router reuses this component instance while navigating, reload for the new route.
onBeforeRouteUpdate(async (to) => {
  const nextId = typeof to.params.id === 'string' ? to.params.id : props.id;
  await loadData(nextId);
});

watch(
  () => formData.selectedSupplier,
  (newVal) => {
    if (newVal && !isLoading.value) {
      errorFields.value.delete('supplier');
      const supplierData = suppliers.value.find((s) => s.name === newVal);
      if (supplierData) {
        currentSupplierNumber.value = supplierData.supplierNumber || '';
        formData.leftColumn.forEach((field) => {
          switch (field.id) {
            case 'f1':
              field.value = supplierData.organisationNumber || '';
              break;
            case 'f2':
              field.paymentType = supplierData.paymentType || 'BG';
              setFormattedFieldValue(
                field,
                field.paymentType === 'BG'
                  ? supplierData.bankGiro
                  : supplierData.postGiro,
              );
              break;
            case 'f3':
              field.value = supplierData.streetName || '';
              break;
            case 'f35':
              field.value = supplierData.buildingNumber || '';
              break;
            case 'f4':
              field.value = supplierData.zipCode || '';
              break;
            case 'f5':
              field.value = supplierData.city || '';
              break;
            case 'f8':
              field.value = supplierData.vatNumber || '';
              break;
          }
        });
      }
    }
  },
);

const selectedVatType = computed(
  () => formData.bottomRows.find((f) => f.id === 'bottom3')?.value,
);

const isVatFieldDisabled = computed(
  () =>
    selectedVatType.value !== VatType.Normal && selectedVatType.value !== '',
);

watch(isVatFieldDisabled, (disabled) => {
  if (disabled) {
    const vatField = formData.rightColumn.find((f) => f.id === 'f14');
    if (vatField) vatField.value = '';
  }
});

// TODO: add method to check the value of Project to verify that
// a project ID is selected before enabling the attest dropwdown
const hasProjectId = ref(false);
const setHasProjectId = (id: string) => {
  if (id === '') hasProjectId.value = false;
  else hasProjectId.value = true;
};

const formatFormDataToApiBody = (data: FormDataStructure) => {
  const getVal = (id: string): string =>
    [...data.leftColumn, ...data.rightColumn].find((f) => f.id === id)?.value ||
    '';
  const getBottomVal = (id: string): string =>
    data.bottomRows.find((f) => f.id === id)?.value || '';

  const paymentField = data.leftColumn.find((f) => f.id === 'f2');
  const type = paymentField?.paymentType;
  const val = paymentField?.value || '';
  const isBg = type === 'BG' || type === t('adminCreateInvoice.paymentType.bg');
  const isPg = type === 'PG' || type === t('adminCreateInvoice.paymentType.pg');

  const parseAmount = (fieldId: string) => {
    const stringVal = getVal(fieldId).replace(/\s/g, '').replace(',', '.');
    return parseFloat(stringVal) || 0;
  };

  const getAccountDetails = () => {
    const selectedId = getBottomVal('bottom1');
    const accountObj = accounts.value.find((acc) => acc.value == selectedId);
    if (accountObj) {
      return {
        number: Number(accountObj.value),
        name: accountObj.text ? accountObj.text.replace(/^\d+\s*-\s*/, '') : '',
      };
    }
    return { number: selectedId ? Number(selectedId) : 0, name: '' };
  };

  const { number: accountNumber, name: accountName } = getAccountDetails();
  const totalAmount = parseAmount('f13');
  const vat = parseAmount('f14');
  const selectedProjectId = getBottomVal('bottom2');
  const pennyAdjust = parseAmount('f125');

  let statusToSend = selectedStatus.value;
  if (!statusToSend && currentStatus.value === 'voided') {
    statusToSend = 'voided';
  }

  // @tag:vera-2.5:solution - the API expects debit and credit values, not netAmount, so we need to convert the totalAmount to netAmount and then to debit/credit
  const { netAmount } = accountingConverter.fromTotalAmountToRows({
    totalAmount,
    vat,
    pennyAdjust,
  });
  const { debit, credit } =
    accountingConverter.fromAmountToDebitCredit(netAmount);

  return {
    supplier: {
      name: data.selectedSupplier,
      supplierNumber: currentSupplierNumber.value,
      organisationNumber: getVal('f1'),
      streetName: getVal('f3'),
      buildingNumber: getVal('f35'),
      zipCode: getVal('f4'),
      city: getVal('f5'),
      bankGiro: isBg && val ? val : null,
      postGiro: isPg && val ? val : null,
      vatNumber: getVal('f8'),
    },
    invoice: {
      fortnoxId: null,
      projectId: selectedProjectId,
      invoiceDate: getVal('f9'),
      dueDate: getVal('f10'),
      termsOfPayment: getVal('f11'),
      ocr: getVal('f12'),
      invoiceNumber: getVal('f15'),
      pennyAdjust: parseAmount('f125'),
      totalAmount: totalAmount,
      vat: parseAmount('f14'),
      currency: getVal('f16'),
      vatType: getBottomVal('bottom3'),
      bankGiro: isBg && val ? val : null,
      postGiro: isPg && val ? val : null,
      notes: data.note,
      accountNumber: accountNumber || null,
      accountName: accountName || null,
      rows: [
        {
          id: data.currentRowId,
          rowIndex: 1,
          accountNumber: accountNumber,
          accountName: accountName || null,
          projectId: selectedProjectId || null,
          // 2@tag:vera-2.5:fixed - these debit and credit values ARE the new debit and credit values on the server
          debit,
          credit,
        },
      ],
      status: statusToSend,
    },
  };
};

const validateRequirement = (
  condition: unknown,
  errorMessage: string,
): boolean => {
  if (!condition) {
    notificationService.open({
      text: errorMessage,
      type: 'danger',
    });
    return false;
  }
  return true;
};

const validateAllFields = (): boolean => {
  const missingFields: string[] = [];
  const errors = new Set<string>();

  // Validate supplier
  if (!formData.selectedSupplier) {
    missingFields.push(t('adminCreateInvoice.chooseSupplier'));
    errors.add('supplier');
  }

  // Validate left column fields
  formData.leftColumn.forEach((field) => {
    if (field.id === 'f2') return;
    if (!field.value || field.value.trim() === '') {
      missingFields.push(field.label);
      errors.add(field.id);
    }
  });

  // Validate right column fields
  formData.rightColumn.forEach((field) => {
    if (field.id === 'f12' || field.id === 'f125') return;
    if (field.id === 'f14' && isVatFieldDisabled.value) return;
    if (!field.value || field.value.trim() === '') {
      missingFields.push(field.label);
      errors.add(field.id);
    }
  });

  // Validate bottom rows fields
  formData.bottomRows.forEach((field) => {
    if (!field.value || field.value.trim() === '') {
      missingFields.push(field.label);
      errors.add(field.id);
    }
  });

  if (missingFields.length > 0) {
    errorFields.value = errors;
    const fieldList = missingFields.join(', ');
    notificationService.open({
      text: `${t('adminCreateInvoice.requiredFields.missingFields')}: ${fieldList}`,
      type: 'danger',
    });
    return false;
  }

  errorFields.value.clear();
  return true;
};

const handleSave = async () => {
  if (isReadOnly.value) {
    return;
  }

  // Validate all required fields
  if (!validateAllFields()) {
    return;
  }

  // Validate status is selected
  if (
    !validateRequirement(
      selectedStatus.value,
      t('adminCreateInvoice.requiredFields.statusRequired'),
    )
  ) {
    return;
  }

  try {
    const apiBody = formatFormDataToApiBody(formData);

    await adminSupplierInvoiceService.updateSupplierInvoice(props.id, apiBody);

    if (selectedStatus.value) {
      currentStatus.value = selectedStatus.value;
    }
    notificationService.open({
      text: t('adminCreateInvoice.invoiceSavedSuccessfully'),
      type: 'success',
    });
  } catch (error) {
    // The duplicate-invoice notification is shown centrally by handleHttpErrors
    // via the notification registry; just suppress our own generic handling.
    if (isClientError(error, 'DUPLICATE_INVOICE_ERROR')) {
      return;
    }
    if (error instanceof FinancialConverterError) {
      const key = `financialConverter.errors.${error.type}`;
      return notificationService.open({
        type: 'danger',
        text: `${t(`${key}.message`)} (${t(`${key}.format`)})`,
      });
    }
    notificationService.open({
      text: t('adminCreateInvoice.invoiceSavedUnsuccessfully'),
      type: 'danger',
    });
  }
};

const handleVoidClick = () => {
  showVoidModal.value = true;
};

const confirmVoid = async () => {
  try {
    await adminSupplierInvoiceService.voidInvoice(props.id);

    currentStatus.value = 'voided';
    selectedStatus.value = '';

    setFieldVal('rightColumn', 'f13', '');
    setFieldVal('rightColumn', 'f14', '');

    notificationService.open({
      text: t('adminEditInvoice.invoiceVoided', 'Fakturan har makulerats'),
      type: 'success',
    });
  } catch (error) {
    console.error('Kunde inte makulera', error);
    notificationService.open({
      text: 'Kunde inte makulera fakturan',
      type: 'danger',
    });
  } finally {
    showVoidModal.value = false;
  }
};
</script>

<template>
  <div v-if="isReadOnly" class="readonly-alert" role="alert">
    {{ t('adminEditInvoice.invoiceBookedInformation') }}
  </div>
  <div class="page-wrapper" :class="{ 'is-readonly': isReadOnly }">
    <div class="card-container">
      <div v-if="isLoading" class="loading-state">
        {{ t('adminEditInvoice.loadingInvoice') }}
      </div>

      <div v-else>
        <div v-if="currentStatus" class="status-badge" :class="currentStatus">
          {{ t(`adminSupplierInvoice.status.${currentStatus}`) }}
        </div>

        <div class="title-section">
          <h2 class="title is-4 page-title">
            {{ t('adminEditInvoice.editInvoice') }}
          </h2>
          <div class="next-number-hint">
            <span class="hint-label">{{
              t('adminCreateInvoice.serialNumber')
            }}</span>
            <span class="hint-value">{{ serialNumber }}</span>
          </div>
        </div>

        <form @submit.prevent="handleSave">
          <div class="header-section">
            <div class="supplier-toggles"></div>
            <div class="existing-supplier-container">
              <div class="select-wrapper input-wrapper-header">
                <DropDown
                  :items="supplierOptions"
                  v-model="formData.selectedSupplier"
                  :label="t('adminCreateInvoice.chooseSupplier')"
                  :disabled="isReadOnly"
                  :class="{ 'field-error': errorFields.has('supplier') }"
                />
              </div>
            </div>
          </div>

          <div class="grid-columns">
            <div class="column">
              <div
                v-for="field in formData.leftColumn"
                :key="field.id"
                class="input-group"
              >
                <label :for="field.id">{{ field.label }}</label>
                <div v-if="field.id === 'f2'" class="joined-input-group">
                  <div class="select-wrapper-compact">
                    <select
                      v-model="field.paymentType"
                      class="compact-select"
                      :disabled="isReadOnly"
                    >
                      <option value="BG">
                        {{ t('adminCreateInvoice.paymentType.bg') }}
                      </option>
                      <option value="PG">
                        {{ t('adminCreateInvoice.paymentType.pg') }}
                      </option>
                    </select>
                    <svg
                      class="select-arrow"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                  <input
                    :id="field.id"
                    :value="field.value"
                    type="text"
                    :placeholder="
                      field.paymentType === 'BG' ? '123-4567' : '98765-4'
                    "
                    :readonly="isReadOnly"
                    :class="{ 'field-error': errorFields.has(field.id) }"
                    @input="handleFieldInput($event, field)"
                  />
                </div>
                <input
                  v-else
                  :id="field.id"
                  :value="field.value"
                  type="text"
                  :readonly="isReadOnly"
                  placeholder="-"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  @input="handleFieldInput($event, field)"
                />
              </div>
            </div>

            <div class="column">
              <div
                v-for="field in formData.rightColumn"
                :key="field.id"
                class="input-group"
              >
                <label :for="field.id">{{ field.label }}</label>

                <template v-if="field.id === 'f16'">
                  <DropDown
                    :items="currencyOptions"
                    v-model="field.value"
                    :label="t('adminCreateInvoice.chooseCurrency')"
                    :disabled="isReadOnly"
                  />
                </template>

                <input
                  v-else-if="field.id === 'f14'"
                  class="compact-select"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  :readonly="isReadOnly"
                  :disabled="isVatFieldDisabled"
                  :id="field.id"
                  type="text"
                  :value="field.value"
                  placeholder="-"
                  @input="handleFieldInput($event, field)"
                />

                <input
                  v-else
                  class="compact-select"
                  :class="{
                    'negative-value':
                      field.id === 'f11' && Number(field.value) < 0,
                    'positive-value':
                      field.id === 'f11' &&
                      Number(field.value) >= 0 &&
                      field.value !== '',
                    'field-error': errorFields.has(field.id),
                  }"
                  :id="field.id"
                  :value="field.value"
                  :type="['f9', 'f10'].includes(field.id) ? 'date' : 'text'"
                  :readonly="isReadOnly"
                  :disabled="field.id === 'f11'"
                  placeholder="-"
                  @input="handleFieldInput($event, field)"
                />
              </div>
            </div>
          </div>

          <div class="coding-section">
            <div
              v-for="field in formData.bottomRows"
              :key="field.id"
              class="input-group"
            >
              <label :for="field.id">{{ field.label }}</label>
              <template v-if="field.id === 'bottom1'">
                <DropDown
                  :items="accounts ?? []"
                  v-model="field.value"
                  :label="t('adminCreateInvoice.chooseAccount')"
                  :disabled="isReadOnly"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  @update:modelValue="errorFields.delete(field.id)"
                />
              </template>
              <template v-else-if="field.id === 'bottom2'">
                <ProjectsDropDown
                  v-model="field.value"
                  :data="{
                    placeholder: t('adminCreateInvoice.chooseProject'),
                    includeArchived: false,
                  }"
                  :mode="isReadOnly ? 'disabled' : 'select'"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  @action="
                    (e) => {
                      errorFields.delete(field.id);
                      setHasProjectId(e.project.id);
                    }
                  "
                />
              </template>
              <template v-else-if="field.id === 'bottom3'">
                <DropDown
                  :items="vatTypeOptions"
                  v-model="field.value"
                  :label="t('adminCreateInvoice.chooseVatType')"
                  :disabled="isReadOnly"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  @update:modelValue="errorFields.delete(field.id)"
                />
              </template>
            </div>
          </div>

          <div class="footer-section">
            <div class="note-area">
              <textarea
                id="notes"
                v-model="formData.note"
                :placeholder="t('adminCreateInvoice.notes')"
                :readonly="isReadOnly"
              ></textarea>
            </div>

            <div v-if="!isReadOnly" class="actions">
              <div class="left-actions">
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="isReadOnly"
                >
                  {{ t('adminCreateInvoice.save') }}
                </button>

                <div class="status-select-wrapper">
                  <DropDown
                    :items="statusOptions"
                    v-model="selectedStatus"
                    :label="t('adminSupplierInvoice.tableHeaders.status')"
                    :disabled="isReadOnly || !hasProjectId"
                  />
                </div>
              </div>

              <button
                type="button"
                class="btn btn-void"
                :disabled="isReadOnly"
                @click="handleVoidClick"
              >
                {{ t('adminEditInvoice.void') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="card-container invoice-preview">
      <InvoiceSkeleton v-if="isLoading" />
      <RenderInvoice
        v-else-if="uploadedFile"
        :file="uploadedFile"
        :change-file="false"
        :loading="isLoading"
      />
      <div v-else class="no-file-placeholder">
        <p>{{ t('adminEditInvoice.noPdfAvailable') }}</p>
      </div>
    </div>

    <div v-if="showVoidModal" class="modal-overlay">
      <div class="modal-box">
        <h3 class="modal-title">{{ t('adminEditInvoice.void') }}?</h3>
        <p class="modal-text">
          {{ t('adminEditInvoice.confirmVoidMessage') }}
        </p>

        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-secondary"
            @click="showVoidModal = false"
          >
            {{ t('adminEditInvoice.cancelVoid') }}
          </button>
          <button type="button" class="btn btn-danger" @click="confirmVoid">
            {{ t('adminEditInvoice.confirmVoid') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-title {
  font-weight: 500;
}

.page-wrapper {
  gap: 20px !important;
  justify-content: flex-start !important;
}

.card-container {
  width: 100%;
  background: linear-gradient(180deg, #eef2f6 0%, #f1f5f9 100%);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  height: fit-content;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 10px 15px -3px rgba(0, 0, 0, 0.1);
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #334155;
  border: 1px solid #cbd5e1;
  position: relative;
  margin-right: 0;

  &.invoice-preview {
    min-width: 400px;
    position: sticky;
    overflow: auto;
  }
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  font-size: 1.1rem;
  color: #64748b;
}

.no-file-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 20px;
  height: 78%;
  width: 90%;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  color: #64748b;
  font-weight: 500;
}

:deep(.render-invoice),
.invoice-preview-container > *:first-child:not(.no-file-placeholder) {
  min-height: 600px;
}

@media (max-width: 1200px) {
  .page-wrapper {
    flex-direction: column;
  }
  .card-container {
    max-width: 100%;
  }
}

.status-badge {
  position: absolute;
  top: 0;
  right: 0;
  padding: 0.5rem 1.5rem;
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: -2px 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 10;
  pointer-events: none;
}

.status-badge.voided {
  background-color: #ef9a9a;
  color: #7f1d1d;
  border-left: 1px solid #e57373;
  border-bottom: 1px solid #e57373;
}

.status-badge.incoming {
  background-color: #f4f5f6;
  color: #334155;
  border-left: 1px solid #cbd5e1;
  border-bottom: 1px solid #cbd5e1;
}

.status-badge.undercertificate {
  background-color: #cbdef8;
  color: #1e3a8a;
  border-left: 1px solid #60a5fa;
  border-bottom: 1px solid #60a5fa;
}

.status-badge.parked {
  background-color: #f8decb;
  color: #9a3412;
  border-left: 1px solid #fb923c;
  border-bottom: 1px solid #fb923c;
}

.status-badge.disputed {
  background-color: #f8cbcb;
  color: #991b1b;
  border-left: 1px solid #f87171;
  border-bottom: 1px solid #f87171;
}

.readonly-alert {
  margin: 0.5rem 0 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid #93c5fd;
  background: #eff6ff;
  color: #1e3a8a;
  font-size: 0.9rem;
  line-height: 1.4;
}

.page-title {
  font-weight: 600;
  margin-bottom: 0.2rem;
  color: #1e293b;
}

.next-number-hint {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
  margin-left: 0;
  margin-top: 0.5rem;
}

.hint-value {
  color: #3b82f6;
  font-weight: 700;
  font-family: monospace;
  font-size: 0.95rem;
}

.grid-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;
}

.coding-section {
  width: 100%;
  min-width: 305px;
  max-width: 180px;
  margin-left: 10px;
}

.select-wrapper-compact {
  position: relative;
  width: 55px;
  flex-shrink: 0;
}

.joined-input-group input[type='text'] {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

.actions {
  display: flex;
  gap: 0.8rem;
  justify-content: space-between;
  align-items: center;
}

.left-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.status-select-wrapper {
  width: 180px;
  position: relative;
}

.status-select-wrapper :deep(ul),
.status-select-wrapper :deep(.items),
.status-select-wrapper :deep(.dropdown-menu),
.status-select-wrapper :deep(.dropdown-content) {
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 5px;
  margin-top: 0;
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.1);
}

.field-error {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
}

.field-error:focus {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2) !important;
}

@media (max-width: 900px) {
  .page-wrapper {
    padding: 1rem;
  }
  .grid-columns {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  .coding-section {
    width: 100%;
  }

  .actions {
    flex-direction: column;
    gap: 1rem;
  }
  .left-actions {
    width: 100%;
    justify-content: space-between;
  }
  .btn-void {
    width: 100%;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.modal-box {
  background: white;
  padding: 1.4rem;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  text-align: center;
  border: 1px solid #e2e8f0;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.modal-text {
  color: #64748b;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.is-readonly :deep(input[readonly]),
.is-readonly :deep(textarea[readonly]) {
  cursor: text;
  background-color: #fff;
  color: inherit;
}

.is-readonly :deep(select:disabled),
.is-readonly :deep(input:disabled),
.is-readonly :deep(textarea:disabled) {
  opacity: 1;
  cursor: text;
  background-color: #fff;
  color: inherit;
}
</style>
